import { useRef, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useService, useCreateService, useUpdateService, useTestServiceConfig, useServiceTypes, useServiceTemplates, useDeleteServiceTemplate } from '../../hooks/useServices';
import { useGroups } from '../../hooks/useGroups';
import { Alert } from '../common/Alert';
import { Button } from '../common/Button';
import { Card } from '../common/Card';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Field } from '../common/Field';
import { Input, Select, Textarea, checkboxClass } from '../common/Input';
import { Spinner } from '../common/Spinner';
import { useToast } from '../common/Toast';
import { useAuth } from '../../context/AuthContext';
import { apiError } from '../../utils/apiError';
import { parseHeaders } from '../../utils/headers';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { AdHocTestResult, Assertion, DnsRecordType, DNS_RECORD_TYPES, ServiceInput, ServiceType, SERVICE_TYPES } from '../../types';
import { AssertionsEditor } from './AssertionsEditor';
import { Diagnostics, summariseTest } from './Diagnostics';

const methods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];
const authTypes = [
  { value: '', label: 'None' },
  { value: 'basic', label: 'Basic Auth' },
  { value: 'bearer', label: 'Bearer Token' },
  { value: 'api-key', label: 'API Key' },
];

/** Fallback when /services/types has not answered (offline, or a test render). */
const TYPE_FALLBACK: { type: ServiceType; label: string; description: string; available: boolean; reason: string | null }[] = [
  { type: 'http', label: 'HTTP(S)', description: 'Request a URL and assert on status, body, JSON, headers and latency', available: true, reason: null },
  { type: 'tcp', label: 'TCP port', description: 'Open a connection to host:port', available: true, reason: null },
  { type: 'dns', label: 'DNS record', description: 'Resolve a name and optionally expect a value', available: true, reason: null },
  { type: 'ping', label: 'Ping (ICMP)', description: 'Echo request to a host', available: true, reason: null },
  { type: 'heartbeat', label: 'Heartbeat (push)', description: 'A job pings PulseCheck; alert when it stops', available: true, reason: null },
  { type: 'group', label: 'Group (composite)', description: 'Up/down from the members of a group', available: true, reason: null },
];

/** Keep the field usable while typing: an empty box must not become NaN. */
function toNumber(raw: string, fallback: number): number {
  const n = parseInt(raw, 10);
  return Number.isNaN(n) ? fallback : n;
}

/**
 * Roughly how long after the first failure an outage is confirmed. The first
 * `retries` extra checks are quick hops `retry_interval` apart; any further
 * ones wait for the regular interval.
 */
function confirmationSeconds(f: { interval: number; retries: number; retry_interval: number; down_after: number }): number {
  const extra = Math.max(0, f.down_after - 1);
  const quick = Math.min(extra, f.retries);
  return quick * f.retry_interval + (extra - quick) * f.interval;
}

function humanSeconds(s: number): string {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  return `${(s / 3600).toFixed(1)} h`;
}

export function ServiceForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const serviceId = id ? parseInt(id, 10) : 0;
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { hasPermission } = useAuth();
  const hasPermissionCreate = hasPermission('services.create');

  const { data: existing, isLoading } = useService(serviceId);
  const { data: groups } = useGroups();
  const { data: typeInfo } = useServiceTypes();
  const createService = useCreateService();
  const updateService = useUpdateService();

  useDocumentTitle(isEdit ? 'Edit service' : 'Add service');

  const pristine = useRef<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const headersRef = useRef<HTMLTextAreaElement>(null);
  const [headersError, setHeadersError] = useState('');

  // On edit the server returns a masked auth_value, never the real secret.
  // Only send a new credential when the user actually types one — otherwise
  // the payload would overwrite the stored secret with null.
  const testConfig = useTestServiceConfig();
  const [testResult, setTestResult] = useState<AdHocTestResult | null>(null);

  const [authTouched, setAuthTouched] = useState(false);
  const [keyTouched, setKeyTouched] = useState(false);
  const hasStoredCredential = Boolean(isEdit && existing?.auth_value);
  const hasStoredKey = Boolean(isEdit && existing?.client_key);

  const [form, setForm] = useState({
    name: '',
    type: 'http' as ServiceType,
    url: '',
    method: 'GET',
    interval: 60,
    timeout: 30000,
    assertions: [{ type: 'status', value: '2xx' }] as Assertion[],
    headers: '{}',
    body: '',
    auth_type: '',
    auth_value: '',
    // transport
    follow_redirects: true,
    max_redirects: 5,
    tls_verify: true,
    ip_version: '' as '' | '4' | '6',
    resolver: '',
    client_cert: '',
    client_key: '',
    cert_expiry_days: '',
    // typed targets
    host: '',
    port: '',
    dns_record_type: 'A' as DnsRecordType,
    dns_expected: '',
    heartbeat_grace: 60,
    group_id: '',
    group_mode: 'all' as 'all' | 'any',
    tags: '',
    enabled: true,
    public: false,
    group_ids: [] as number[],
    // String, not number: a blank box must be representable so it can mean
    // "inherit the global default" rather than being coerced to a fallback.
    sla_target: '',
    // Alert confirmation. Defaults match the server's: ~40s to confirm an
    // outage on a 60s interval while absorbing a one-off blip.
    retries: 2,
    retry_interval: 20,
    down_after: 3,
    up_after: 1,
    degraded_threshold_ms: '',
  });

  useEffect(() => {
    if (existing && isEdit) {
      setForm({
        name: existing.name,
        type: existing.type ?? 'http',
        url: existing.type === 'http' || !existing.type ? existing.url : '',
        method: existing.method,
        interval: existing.interval,
        timeout: existing.timeout,
        assertions: existing.assertions?.length ? existing.assertions : [{ type: 'status', value: String(existing.expected_status ?? 200) }],
        headers: existing.headers ? JSON.stringify(existing.headers, null, 2) : '{}',
        body: existing.body || '',
        auth_type: existing.auth_type || '',
        auth_value: '',
        follow_redirects: existing.follow_redirects !== false,
        max_redirects: existing.max_redirects ?? 5,
        tls_verify: existing.tls_verify !== false,
        ip_version: existing.ip_version ?? '',
        resolver: existing.resolver ?? '',
        client_cert: existing.client_cert ?? '',
        client_key: '',
        cert_expiry_days: existing.cert_expiry_days === null || existing.cert_expiry_days === undefined ? '' : String(existing.cert_expiry_days),
        host: existing.host ?? '',
        port: existing.port === null || existing.port === undefined ? '' : String(existing.port),
        dns_record_type: existing.dns_record_type ?? 'A',
        dns_expected: existing.dns_expected ?? '',
        heartbeat_grace: existing.heartbeat_grace ?? 60,
        group_id: existing.group_id === null || existing.group_id === undefined ? '' : String(existing.group_id),
        group_mode: existing.group_mode ?? 'all',
        tags: existing.tags?.join(', ') || '',
        enabled: existing.enabled,
        public: existing.public ?? false,
        group_ids: existing.group_ids || [],
        sla_target: existing.sla_target?.toString() ?? '',
        retries: existing.retries ?? 2,
        retry_interval: existing.retry_interval ?? 20,
        down_after: existing.down_after ?? 3,
        up_after: existing.up_after ?? 1,
        degraded_threshold_ms: existing.degraded_threshold_ms === null || existing.degraded_threshold_ms === undefined
          ? '' : String(existing.degraded_threshold_ms),
      });
      setAuthTouched(false);
      setKeyTouched(false);
    }
  }, [existing, isEdit]);

  // Capture the baseline once the form has its initial values.
  useEffect(() => {
    if (pristine.current === null && (!isEdit || existing)) {
      pristine.current = JSON.stringify(form);
    }
  }, [form, existing, isEdit]);

  const dirty = pristine.current !== null && JSON.stringify(form) !== pristine.current;
  useUnsavedChanges(dirty);

  if (isEdit && isLoading) {
    return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  }

  const types = typeInfo ?? TYPE_FALLBACK;
  const typeMeta = types.find(t => t.type === form.type) ?? TYPE_FALLBACK[0];
  const isHttp = form.type === 'http';
  const isHeartbeat = form.type === 'heartbeat';
  const isGroup = form.type === 'group';
  const needsHost = form.type === 'tcp' || form.type === 'dns' || form.type === 'ping';
  const usesNetwork = isHttp || needsHost;

  /**
   * Switching type: keep everything shared, reset what the new type cannot
   * use, and give a composite the confirmation defaults its children have
   * already earned.
   */
  const switchType = (next: ServiceType) => {
    setForm(f => ({
      ...f,
      type: next,
      ...(next === 'group' && f.type !== 'group' ? { retries: 0, down_after: 1, up_after: 1 } : {}),
      ...(f.type === 'group' && next !== 'group' ? { retries: 2, down_after: 3, up_after: 1 } : {}),
      ...(next === 'heartbeat' && f.type !== 'heartbeat' ? { interval: Math.max(f.interval, 300) } : {}),
    }));
    setTestResult(null);
  };

  /** The payload for both the test and the save, minus credentials. */
  const buildPayload = (): ServiceInput | null => {
    const headers = parseHeaders(form.headers);
    if (isHttp && !headers.ok) {
      setHeadersError(headers.message);
      headersRef.current?.focus();
      return null;
    }
    const data: ServiceInput = {
      name: form.name,
      type: form.type,
      interval: form.interval,
      timeout: form.timeout,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      enabled: form.enabled,
      public: form.public,
      group_ids: form.group_ids,
      sla_target: form.sla_target === '' ? null : Number(form.sla_target),
      retries: form.retries,
      retry_interval: form.retry_interval,
      down_after: form.down_after,
      up_after: form.up_after,
      degraded_threshold_ms: form.degraded_threshold_ms === '' ? null : Number(form.degraded_threshold_ms),
      resolver: usesNetwork && form.resolver.trim() ? form.resolver.trim() : null,
      ip_version: usesNetwork && form.ip_version ? form.ip_version : null,
    };
    if (isHttp) {
      Object.assign(data, {
        url: form.url,
        method: form.method,
        assertions: form.assertions,
        headers: headers.ok ? headers.value : null,
        body: form.body || null,
        auth_type: form.auth_type || null,
        follow_redirects: form.follow_redirects,
        max_redirects: form.max_redirects,
        tls_verify: form.tls_verify,
        client_cert: form.client_cert.trim() || null,
        cert_expiry_days: form.cert_expiry_days === '' ? null : Number(form.cert_expiry_days),
      });
    }
    if (needsHost) data.host = form.host.trim();
    if (form.type === 'tcp') data.port = form.port === '' ? null : Number(form.port);
    if (form.type === 'dns') {
      data.dns_record_type = form.dns_record_type;
      data.dns_expected = form.dns_expected.trim() || null;
    }
    if (isHeartbeat) data.heartbeat_grace = form.heartbeat_grace;
    if (isGroup) {
      data.group_id = form.group_id === '' ? null : Number(form.group_id);
      data.group_mode = form.group_mode;
    }
    return data;
  };

  /**
   * Try the check with the values currently on screen, without saving.
   * `POST /services/:id/test` could only re-run an already-saved service, so a
   * new service could never be verified before creating it.
   */
  const handleTest = () => {
    const data = buildPayload();
    if (!data) return;
    if (isHttp && !form.url) { addToast('error', 'Enter a URL first.'); return; }
    if (needsHost && !form.host) { addToast('error', 'Enter a host first.'); return; }
    if (form.type === 'tcp' && form.port === '') { addToast('error', 'Enter a port first.'); return; }
    if (isGroup && form.group_id === '') { addToast('error', 'Choose a group first.'); return; }

    // Credentials: the on-screen value if typed, else nothing (the server
    // cannot test with a stored secret it never returns).
    if (isHttp) {
      data.auth_value = form.auth_value || null;
      data.client_key = form.client_key || null;
    }

    setTestResult(null);
    testConfig.mutate(data, {
      onSuccess: setTestResult,
      onError: err => addToast('error', apiError(err, 'Could not run the test')),
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const data = buildPayload();
    if (!data) {
      addToast('error', 'Headers are not valid JSON — nothing was saved.');
      return;
    }
    setHeadersError('');

    if (isHttp) {
      if (!form.auth_type) {
        // Auth switched off — clearing the stored credential is the intent.
        data.auth_value = null;
      } else if (!isEdit || authTouched) {
        data.auth_value = form.auth_value || null;
      }
      // Otherwise auth_value is omitted entirely, and the server leaves the
      // existing encrypted credential untouched. The client key works the same way.
      if (!form.client_cert.trim()) {
        data.client_key = null;
      } else if (!isEdit || keyTouched) {
        data.client_key = form.client_key || null;
      }
    }

    if (isEdit) {
      updateService.mutate({ id: serviceId, data }, {
        onSuccess: () => { addToast('success', 'Service updated'); navigate(`/services/${serviceId}`); },
        onError: err => addToast('error', apiError(err, 'Failed to update service')),
      });
    } else {
      createService.mutate(data, {
        onSuccess: (svc) => { addToast('success', 'Service created'); navigate(`/services/${svc.id}`); },
        onError: err => addToast('error', apiError(err, 'Failed to create service')),
      });
    }
  };

  const update = (field: string, value: any) => setForm(f => ({ ...f, [field]: value }));

  // ── Templates (create only) ──────────────────────────────────────────────
  const { data: templates } = useServiceTemplates();
  const deleteTemplate = useDeleteServiceTemplate();
  const [templateId, setTemplateId] = useState('');
  const str = (v: unknown, fallback: string) => (v === null || v === undefined ? fallback : String(v));

  /**
   * Lay a template's payload over the form. Only the fields the template
   * carries change; name and the target stay yours. Credentials are never in
   * a template, so those boxes are left as they are.
   */
  const applyTemplate = (payload: Record<string, any>) => setForm(f => ({
    ...f,
    type: payload.type ?? f.type,
    url: payload.url ?? f.url,
    method: payload.method ?? f.method,
    interval: payload.interval ?? f.interval,
    timeout: payload.timeout ?? f.timeout,
    assertions: Array.isArray(payload.assertions) && payload.assertions.length ? payload.assertions : f.assertions,
    headers: payload.headers ? JSON.stringify(payload.headers, null, 2) : f.headers,
    body: payload.body ?? f.body,
    auth_type: payload.auth_type ?? f.auth_type,
    follow_redirects: payload.follow_redirects ?? f.follow_redirects,
    max_redirects: payload.max_redirects ?? f.max_redirects,
    tls_verify: payload.tls_verify ?? f.tls_verify,
    ip_version: payload.ip_version ?? f.ip_version,
    resolver: payload.resolver ?? f.resolver,
    client_cert: payload.client_cert ?? f.client_cert,
    cert_expiry_days: 'cert_expiry_days' in payload ? str(payload.cert_expiry_days, '') : f.cert_expiry_days,
    host: payload.host ?? f.host,
    port: 'port' in payload ? str(payload.port, '') : f.port,
    dns_record_type: payload.dns_record_type ?? f.dns_record_type,
    dns_expected: payload.dns_expected ?? f.dns_expected,
    heartbeat_grace: payload.heartbeat_grace ?? f.heartbeat_grace,
    group_id: 'group_id' in payload ? str(payload.group_id, '') : f.group_id,
    group_mode: payload.group_mode ?? f.group_mode,
    tags: Array.isArray(payload.tags) ? payload.tags.join(', ') : f.tags,
    enabled: payload.enabled ?? f.enabled,
    public: payload.public ?? f.public,
    group_ids: Array.isArray(payload.group_ids) ? payload.group_ids : f.group_ids,
    sla_target: 'sla_target' in payload ? str(payload.sla_target, '') : f.sla_target,
    retries: payload.retries ?? f.retries,
    retry_interval: payload.retry_interval ?? f.retry_interval,
    down_after: payload.down_after ?? f.down_after,
    up_after: payload.up_after ?? f.up_after,
    degraded_threshold_ms: 'degraded_threshold_ms' in payload ? str(payload.degraded_threshold_ms, '') : f.degraded_threshold_ms,
  }));

  const chooseTemplate = (value: string) => {
    setTemplateId(value);
    const t = templates?.find(x => String(x.id) === value);
    if (t) {
      applyTemplate(t.payload as Record<string, any>);
      addToast('info', `Applied template "${t.name}"`);
    }
  };

  const authLabel =
    form.auth_type === 'basic' ? 'Credentials (user:pass)'
    : form.auth_type === 'bearer' ? 'Token'
    : 'API Key';

  const hostLabel = form.type === 'dns' ? 'Hostname to resolve' : 'Host';
  const hostPlaceholder = form.type === 'dns' ? 'example.com' : form.type === 'tcp' ? 'db.internal or 10.0.0.5' : 'gateway.local or 192.168.1.1';

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
      <h1 className="text-2xl font-bold">{isEdit ? 'Edit Service' : 'Add Service'}</h1>

      {!isEdit && (templates?.length ?? 0) > 0 && (
        <Card className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-semibold">Start from a template</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Pre-fills everything below except the name and any credentials. Save one from a service's <em>More → Save as template</em>.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={templateId} onChange={e => chooseTemplate(e.target.value)} aria-label="Template">
              <option value="">No template</option>
              {templates!.map(t => <option key={t.id} value={t.id}>{t.name}{t.description ? ` — ${t.description}` : ''}</option>)}
            </Select>
            {templateId && hasPermissionCreate && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                loading={deleteTemplate.isPending}
                onClick={() => {
                  const t = templates!.find(x => String(x.id) === templateId);
                  deleteTemplate.mutate(parseInt(templateId, 10), {
                    onSuccess: () => { setTemplateId(''); addToast('success', `Deleted template "${t?.name}"`); },
                    onError: err => addToast('error', apiError(err, 'Could not delete the template')),
                  });
                }}
              >
                Delete template
              </Button>
            )}
          </div>
        </Card>
      )}

      <Card className="p-4 space-y-4">
        <h2 className="font-semibold">Basic Info</h2>

        <Field label="Name" required>
          {p => (
            <Input
              {...p}
              type="text"
              required
              value={form.name}
              onChange={e => update('name', e.target.value)}
              placeholder="My API Service"
            />
          )}
        </Field>

        <Field
          label="Check type"
          hint={typeMeta.available ? typeMeta.description : `${typeMeta.description}. Not available here: ${typeMeta.reason ?? 'unsupported on this host'}.`}
        >
          {p => (
            <Select {...p} value={form.type} onChange={e => switchType(e.target.value as ServiceType)}>
              {SERVICE_TYPES.map(t => {
                const meta = types.find(x => x.type === t) ?? TYPE_FALLBACK.find(x => x.type === t)!;
                return <option key={t} value={t}>{meta.label}{meta.available ? '' : ' (unavailable)'}</option>;
              })}
            </Select>
          )}
        </Field>

        {isHttp && (
          <Field label="URL" required>
            {p => (
              <Input
                {...p}
                type="url"
                required
                value={form.url}
                onChange={e => update('url', e.target.value)}
                placeholder="https://api.example.com/health"
              />
            )}
          </Field>
        )}

        {needsHost && (
          <div className={`grid gap-4 ${form.type === 'tcp' ? 'grid-cols-[minmax(0,2fr)_minmax(0,1fr)]' : form.type === 'dns' ? 'grid-cols-[minmax(0,2fr)_minmax(0,1fr)]' : 'grid-cols-1'}`}>
            <Field label={hostLabel} required>
              {p => (
                <Input {...p} type="text" required value={form.host} onChange={e => update('host', e.target.value)} placeholder={hostPlaceholder} className="font-mono" />
              )}
            </Field>
            {form.type === 'tcp' && (
              <Field label="Port" required>
                {p => (
                  <Input {...p} type="number" required min={1} max={65535} value={form.port} onChange={e => update('port', e.target.value)} placeholder="5432" className="font-mono" />
                )}
              </Field>
            )}
            {form.type === 'dns' && (
              <Field label="Record type">
                {p => (
                  <Select {...p} value={form.dns_record_type} onChange={e => update('dns_record_type', e.target.value)}>
                    {DNS_RECORD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </Select>
                )}
              </Field>
            )}
          </div>
        )}

        {form.type === 'dns' && (
          <Field label="Expected answer" hint="Optional. A/AAAA/CNAME: a record must equal it. MX: an exchange must match. TXT: a record must contain it. Blank = any answer passes.">
            {p => (
              <Input {...p} type="text" value={form.dns_expected} onChange={e => update('dns_expected', e.target.value)} placeholder="203.0.113.10" className="font-mono" />
            )}
          </Field>
        )}

        {isGroup && (
          <div className="grid grid-cols-2 gap-4">
            <Field label="Group" required hint="The members of this group are the children.">
              {p => (
                <Select {...p} required value={form.group_id} onChange={e => update('group_id', e.target.value)}>
                  <option value="">Choose a group…</option>
                  {(groups ?? []).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </Select>
              )}
            </Field>
            <Field label="Up when" hint="Children that are pending or in maintenance are ignored.">
              {p => (
                <Select {...p} value={form.group_mode} onChange={e => update('group_mode', e.target.value)}>
                  <option value="all">all children are up</option>
                  <option value="any">any child is up</option>
                </Select>
              )}
            </Field>
          </div>
        )}

        {isHttp && (
          <div className="grid grid-cols-2 gap-4">
            <Field label="Method">
              {p => (
                <Select {...p} value={form.method} onChange={e => update('method', e.target.value)}>
                  {methods.map(m => <option key={m} value={m}>{m}</option>)}
                </Select>
              )}
            </Field>
            <Field label="Timeout (ms)">
              {p => (
                <Input {...p} type="number" min={1000} max={60000} value={form.timeout} onChange={e => update('timeout', toNumber(e.target.value, 30000))} />
              )}
            </Field>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Field
            label={isHeartbeat ? 'Expected every (seconds)' : 'Interval (seconds)'}
            hint={isHeartbeat ? 'How often the job should ping. 3600 for hourly, 86400 for daily.' : isGroup ? 'Also re-evaluated the moment a child changes.' : undefined}
          >
            {p => (
              <Input {...p} type="number" min={10} max={604800} value={form.interval} onChange={e => update('interval', toNumber(e.target.value, 60))} />
            )}
          </Field>

          {isHeartbeat ? (
            <Field label="Grace period (seconds)" hint="How late a ping may be before the job counts as missing.">
              {p => (
                <Input {...p} type="number" min={0} max={604800} value={form.heartbeat_grace} onChange={e => update('heartbeat_grace', toNumber(e.target.value, 60))} />
              )}
            </Field>
          ) : !isHttp && !isGroup ? (
            <Field label="Timeout (ms)">
              {p => (
                <Input {...p} type="number" min={1000} max={60000} value={form.timeout} onChange={e => update('timeout', toNumber(e.target.value, 30000))} />
              )}
            </Field>
          ) : null}
        </div>

        {isHeartbeat && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            The push URL is issued when the service is saved; it appears on the service page with a curl example.
            Point the job at it and PulseCheck alerts when the pings stop.
          </p>
        )}
      </Card>

      {isHttp && (
        <Card className="p-4 space-y-4">
          <div>
            <h2 className="font-semibold">Assertions</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              The response must satisfy every line to count as up. Status accepts codes, classes and ranges
              (<span className="font-mono">200</span>, <span className="font-mono">2xx</span>, <span className="font-mono">200-299</span>, <span className="font-mono">200,301</span>).
            </p>
          </div>
          <AssertionsEditor value={form.assertions} onChange={v => update('assertions', v)} />
        </Card>
      )}

      {isHttp && (
        <Card className="p-4 space-y-4">
          <h2 className="font-semibold">Headers &amp; Body</h2>

          <Field
            label="Headers (JSON)"
            error={headersError}
            hint={headersError ? undefined : 'A JSON object of header names to string values.'}
          >
            {p => (
              <Textarea
                {...p}
                ref={headersRef}
                value={form.headers}
                onChange={e => {
                  update('headers', e.target.value);
                  if (headersError) setHeadersError('');
                }}
                onBlur={e => {
                  const result = parseHeaders(e.target.value);
                  setHeadersError(result.ok ? '' : result.message);
                }}
                rows={3}
                className="font-mono"
                placeholder='{"Content-Type": "application/json"}'
              />
            )}
          </Field>

          <Field label="Body">
            {p => (
              <Textarea {...p} value={form.body} onChange={e => update('body', e.target.value)} rows={3} className="font-mono" placeholder='{"key": "value"}' />
            )}
          </Field>
        </Card>
      )}

      {isHttp && (
        <Card className="p-4 space-y-4">
          <h2 className="font-semibold">Authentication</h2>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Auth Type">
              {p => (
                <Select {...p} value={form.auth_type} onChange={e => update('auth_type', e.target.value)}>
                  {authTypes.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                </Select>
              )}
            </Field>

            {form.auth_type && (
              <Field
                label={authLabel}
                hint={hasStoredCredential && !authTouched ? 'A credential is stored. Leave blank to keep it.' : undefined}
              >
                {p => (
                  <Input
                    {...p}
                    type="password"
                    autoComplete="new-password"
                    value={form.auth_value}
                    onChange={e => { setAuthTouched(true); update('auth_value', e.target.value); }}
                    placeholder={hasStoredCredential ? '••••••••' : ''}
                  />
                )}
              </Field>
            )}
          </div>
        </Card>
      )}

      {usesNetwork && (
        <Card className="p-4 space-y-4">
          <div>
            <h2 className="font-semibold">Transport</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              How the connection is made. Leave alone unless the target is behind a redirect, uses an internal CA, or lives on a split-horizon DNS.
            </p>
          </div>

          {isHttp && (
            <div className="grid grid-cols-2 gap-4 items-end">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.follow_redirects} onChange={e => update('follow_redirects', e.target.checked)} className={checkboxClass} />
                Follow redirects
                <span className="text-xs text-gray-500 dark:text-gray-400">(off: a 301 can be the expected status)</span>
              </label>
              <Field label="Max redirects">
                {p => (
                  <Input {...p} type="number" min={0} max={20} disabled={!form.follow_redirects} value={form.max_redirects} onChange={e => update('max_redirects', Math.min(20, Math.max(0, toNumber(e.target.value, 5))))} />
                )}
              </Field>
            </div>
          )}

          {isHttp && (
            <label className="inline-flex items-start gap-2 text-sm">
              <input type="checkbox" checked={form.tls_verify} onChange={e => update('tls_verify', e.target.checked)} className={`${checkboxClass} mt-0.5`} />
              <span>
                Verify the TLS certificate
                <span className="block text-xs text-gray-500 dark:text-gray-400">
                  Turn off for self-signed or internal-CA certificates. Expiry warnings still work either way.
                </span>
              </span>
            </label>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Field label="IP version">
              {p => (
                <Select {...p} value={form.ip_version} onChange={e => update('ip_version', e.target.value)}>
                  <option value="">Either (resolver order)</option>
                  <option value="4">IPv4 only</option>
                  <option value="6">IPv6 only</option>
                </Select>
              )}
            </Field>
            <Field label="DNS resolver" hint="Blank uses the system resolver. ip or ip:port.">
              {p => (
                <Input {...p} type="text" value={form.resolver} onChange={e => update('resolver', e.target.value)} placeholder="192.168.1.2" className="font-mono" />
              )}
            </Field>
          </div>

          {isHttp && (
            <Field label="Warn before certificate expiry (days)" hint="Blank uses the server default (CERT_EXPIRY_DAYS); 0 turns the warning off for this service.">
              {p => (
                <Input {...p} type="number" min={0} max={365} value={form.cert_expiry_days} onChange={e => update('cert_expiry_days', e.target.value)} placeholder="server default" />
              )}
            </Field>
          )}

          {isHttp && (
            <details className="text-sm" open={Boolean(form.client_cert)}>
              <summary className="cursor-pointer font-medium">Client certificate (mutual TLS)</summary>
              <div className="mt-3 space-y-3">
                <Field label="Certificate (PEM)">
                  {p => (
                    <Textarea {...p} rows={4} className="font-mono text-xs" value={form.client_cert} onChange={e => update('client_cert', e.target.value)} placeholder="-----BEGIN CERTIFICATE-----" />
                  )}
                </Field>
                <Field
                  label="Private key (PEM)"
                  hint={hasStoredKey && !keyTouched ? 'A key is stored. Leave blank to keep it.' : 'Stored encrypted; never shown again.'}
                >
                  {p => (
                    <Textarea
                      {...p}
                      rows={4}
                      className="font-mono text-xs"
                      value={form.client_key}
                      onChange={e => { setKeyTouched(true); update('client_key', e.target.value); }}
                      placeholder={hasStoredKey ? '••••••••' : '-----BEGIN PRIVATE KEY-----'}
                    />
                  )}
                </Field>
              </div>
            </details>
          )}
        </Card>
      )}

      <Card className="p-4 space-y-4">
        <div>
          <h2 className="font-semibold">Alerting</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {isGroup
              ? 'Children are already confirmed by their own thresholds, so a composite usually needs none of its own.'
              : isHeartbeat
                ? 'A missed deadline triggers quick re-checks, and the status only changes once enough checks in a row agree.'
                : 'One failed request is not an outage. A failure triggers quick re-checks, and the status only changes once enough checks in a row agree.'}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Quick retries after a failure" hint="0–10. Each runs before the next regular check.">
            {p => (
              <Input {...p} type="number" min={0} max={10} value={form.retries} onChange={e => update('retries', Math.min(10, Math.max(0, toNumber(e.target.value, 2))))} />
            )}
          </Field>
          <Field label="Retry interval (seconds)">
            {p => (
              <Input {...p} type="number" min={5} max={3600} value={form.retry_interval} onChange={e => update('retry_interval', toNumber(e.target.value, 20))} />
            )}
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Down after (consecutive failures)" hint="1 alerts on the first failure.">
            {p => (
              <Input {...p} type="number" min={1} max={20} value={form.down_after} onChange={e => update('down_after', Math.min(20, Math.max(1, toNumber(e.target.value, 3))))} />
            )}
          </Field>
          <Field label="Up after (consecutive passes)" hint="Raise it for services that flap.">
            {p => (
              <Input {...p} type="number" min={1} max={20} value={form.up_after} onChange={e => update('up_after', Math.min(20, Math.max(1, toNumber(e.target.value, 1))))} />
            )}
          </Field>
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
          With these settings an outage is confirmed about{' '}
          <span className="font-medium text-gray-700 dark:text-gray-300">{humanSeconds(confirmationSeconds(form))}</span>{' '}
          after the first failed check{isHeartbeat ? ' (the first check is at the deadline)' : ''}.
        </p>

        {!isHeartbeat && !isGroup && (
          <Field
            label="Degraded above (ms)"
            hint="A passing check slower than this counts as degraded. Blank uses the server default (DEGRADED_THRESHOLD_MS); 0 turns it off for this service."
          >
            {p => (
              <Input {...p} type="number" min={0} max={600000} step={100} value={form.degraded_threshold_ms} onChange={e => update('degraded_threshold_ms', e.target.value)} placeholder="server default" />
            )}
          </Field>
        )}
      </Card>

      <Card className="p-4 space-y-4">
        <h2 className="font-semibold">Organization</h2>

        <Field label="Tags" hint="Comma-separated.">
          {p => (
            <Input {...p} type="text" value={form.tags} onChange={e => update('tags', e.target.value)} placeholder="production, api, critical" />
          )}
        </Field>

        <Field label="Uptime target (%)" hint="Leave blank to use the system default.">
          {p => (
            <Input {...p} type="number" step="0.01" min="0" max="100" value={form.sla_target} onChange={e => update('sla_target', e.target.value)} placeholder="e.g. 99.9" />
          )}
        </Field>

        {groups && groups.length > 0 && (
          <fieldset>
            <legend className="block text-sm font-medium mb-1">Groups</legend>
            <div className="flex flex-wrap gap-2">
              {groups.map(g => (
                <label key={g.id} className="inline-flex items-center gap-1.5 text-sm">
                  <input
                    type="checkbox"
                    checked={form.group_ids.includes(g.id)}
                    onChange={e => {
                      if (e.target.checked) update('group_ids', [...form.group_ids, g.id]);
                      else update('group_ids', form.group_ids.filter(id => id !== g.id));
                    }}
                    className={checkboxClass}
                  />
                  {g.name}
                </label>
              ))}
            </div>
          </fieldset>
        )}

        <div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.enabled} onChange={e => update('enabled', e.target.checked)} className={checkboxClass} />
            Enabled (start monitoring immediately)
          </label>
        </div>

        <div>
          <label className="inline-flex items-start gap-2 text-sm">
            <input type="checkbox" checked={form.public} onChange={e => update('public', e.target.checked)} className={`${checkboxClass} mt-0.5`} />
            <span>
              Show on the default status page
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Adds this service to the page at /status. Other pages, sections and display names are
                managed under Status pages. The target, headers and error messages are never exposed.
              </span>
            </span>
          </label>
        </div>
      </Card>

      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-semibold">Test</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Run this check once with these settings. Nothing is saved and no alerts fire.
            </p>
          </div>
          <Button type="button" variant="secondary" loading={testConfig.isPending} onClick={handleTest}>
            {testConfig.isPending ? 'Testing…' : 'Run test'}
          </Button>
        </div>

        {testResult && (
          <div className="space-y-3">
            <Alert tone={testResult.ok ? 'success' : testResult.status === 'degraded' || testResult.status === 'pending' ? 'warning' : 'error'}>
              {summariseTest(testResult.type ?? form.type, testResult)}
            </Alert>
            {testResult.details && <Diagnostics details={testResult.details} compact />}
          </div>
        )}
      </Card>

      <div className="flex gap-3">
        <Button type="submit" loading={createService.isPending || updateService.isPending}>
          {isEdit ? 'Update Service' : 'Create Service'}
        </Button>
        <Button type="button" variant="secondary" onClick={() => (dirty ? setConfirmDiscard(true) : navigate(-1))}>
          Cancel
        </Button>
      </div>

      <ConfirmDialog
        open={confirmDiscard}
        title="Discard changes?"
        message="This form has unsaved changes. Leaving now will lose them."
        confirmLabel="Discard changes"
        onConfirm={() => { setConfirmDiscard(false); navigate(-1); }}
        onClose={() => setConfirmDiscard(false)}
      />
    </form>
  );
}
